import { useQuery } from '@apollo/client';
import { Order_By } from '@openx/types/gqlTypes.ts';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAuth } from '../../../../../../components/AuthContext/context.ts';
import { GetDrawerSegmentsDocument } from '../../../../../../graphql/query/audiences/GetDrawerSegments.generated.tsx';
import { isAdmin } from '../../../../../../permissions/validators.ts';
import { exportType } from '../../../constants.ts';
import { AudienceFields, type AudienceForm, type FormSegment } from '../../../types.ts';
import { constructSegmentFilterParams } from '../components/drawer/components/SegmentsList/utils/constructSegmentFilterParams.ts';
import { dataSourceOptions } from '../components/drawer/components/types.ts';
import type { UseSegments } from './types.ts';
import { buildSegmentFilters } from './utils/filterBuilder.ts';

export const useSegments: UseSegments<FormSegment> = ({
  accountId = '',
  providerId = '',
  filters = {},
  marketContext,
  linkedProviders,
  dataSourceFilters = dataSourceOptions.All,
}) => {
  const { control } = useFormContext<AudienceForm>();
  const { session } = useAuth();
  const segmentParams = useMemo(
    () => constructSegmentFilterParams({ filters, searchPhrase: filters.phrase || '' }),
    [filters]
  );

  const observableExportType = useWatch({ name: AudienceFields.ExportType, control });

  const isOaMatchType = observableExportType === exportType.OA_MATCH;

  const { where, whereAggregate } = useMemo(
    () =>
      buildSegmentFilters({
        filters: segmentParams,
        accountId,
        providerId,
        linkedProviders,
        marketContext,
        isOaMatchType,
        exportType: observableExportType ?? undefined,
        dataSourceFilters,
      }),
    [
      accountId,
      providerId,
      linkedProviders,
      segmentParams,
      marketContext,
      isOaMatchType,
      dataSourceFilters,
      observableExportType,
    ]
  );

  const {
    loading,
    error,
    data: { segment = [], segment_aggregate } = {},
    refetch,
  } = useQuery(GetDrawerSegmentsDocument, {
    variables: {
      where,
      whereAggregate,
      limit: filters.limit || 100,
      offset: segmentParams.offset || 0,
      order_by: [{ category: Order_By.Desc }],
      ...(isAdmin(session) && { accountId }),
    },
    notifyOnNetworkStatusChange: true,
  });

  const segments = segment as FormSegment[];

  return {
    loading,
    error,
    segments,
    totalCount: segment_aggregate?.aggregate?.count || 0,
    refetch,
  };
};
